import { ADVANCED_ABORT_SIGNAL_PRIVATE } from './privates';
import { AbortNotification } from '../abort-notification';
import { IsObject } from '../../../helpers';
import { Finally, NEVER_PROMISE, PromiseTry, VOID_PROMISE } from '../../../promises/helpers';
import { AdvancedAbortController } from '../implementation';
import { IsAdvancedAbortController } from '../constructor';
export function AdvancedAbortSignalAbort(instance, reason = void 0) {
    const privates = instance[ADVANCED_ABORT_SIGNAL_PRIVATE];
    if (!privates.aborted) {
        privates.aborted = true;
        privates.reason = reason;
        privates.context.emit(new AbortNotification(reason));
    }
}
export function AdvancedAbortSignalNormalizeWrapPromiseOptions(options) {
    const _options = {};
    if (options === void 0) {
        options = {};
    }
    else if (!IsObject(options)) {
        throw new TypeError(`Expected object or void as options`);
    }
    if (options.strategy === void 0) {
        _options.strategy = 'never';
    }
    else if (['resolve', 'reject', 'never'].includes(options.strategy)) {
        _options.strategy = options.strategy;
    }
    else {
        throw new TypeError(`Expected 'resolve', 'reject', 'never' or void as options.strategy`);
    }
    if (options.onAborted === void 0) {
        _options.onAborted = void 0;
    }
    else if (typeof options.onAborted === 'function') {
        _options.onAborted = options.onAborted;
    }
    else {
        throw new TypeError(`Expected function or void as options.onAborted`);
    }
    if (options.onAbortedController === void 0) {
        if (_options.onAborted !== void 0) {
            _options.onAbortedController = new AdvancedAbortController();
        }
    }
    else if (IsAdvancedAbortController(options.onAbortedController)) {
        if (_options.onAborted === void 0) {
            throw new Error(`options.onAbortedController is defined but options.onAborted is missing`);
        }
        else {
            _options.onAbortedController = options.onAbortedController;
        }
    }
    else {
        throw new TypeError(`Expected AdvancedAbortController or void as options.onAbortedController`);
    }
    return _options;
}
export function ApplyAbortStrategy(strategy, reason) {
    switch (strategy) {
        case void 0:
        case 'never':
            return NEVER_PROMISE;
        case 'resolve':
            return VOID_PROMISE;
        case 'reject':
            return Promise.reject(reason);
        default:
            throw new TypeError(`Unexpected strategy: ${strategy}`);
    }
}
export function ApplyAbortStrategyUsingAdvancedAbortSignalReason(instance, strategy, reason = instance.reason) {
    return ApplyAbortStrategy(strategy, reason);
}
export function ApplyOnAbortCallback(instance, options) {
    if (typeof options.onAborted === 'function') {
        const newController = options.onAbortedController;
        return newController.signal.wrapPromise(PromiseTry(() => options.onAborted.call(instance, instance.reason, newController)), {
            strategy: options.strategy,
        });
    }
    else {
        return ApplyAbortStrategyUsingAdvancedAbortSignalReason(instance, options.strategy);
    }
}
export function RaceAborted(instance, promise) {
    let observer;
    return Promise.race([
        new Promise((resolve) => {
            if (instance.aborted) {
                resolve();
            }
            else {
                observer = instance.addListener('abort', () => {
                    resolve();
                });
                observer.activate();
            }
        }),
        promise
    ])
        .then(...Finally(() => {
        if (observer !== void 0) {
            observer.deactivate();
        }
    }));
}
export function ExtractSignalFromFetchArguments(requestInfo, requestInit = {}) {
    if (IsObject(globalThis) && ('AbortController' in globalThis)) {
        if (requestInit.signal instanceof AbortSignal) {
            return requestInit.signal;
        }
        else if ((requestInfo instanceof Request)
            && (requestInfo.signal instanceof AbortSignal)) {
            return requestInfo.signal;
        }
        else {
            return null;
        }
    }
    else {
        return null;
    }
}
export function LinkAdvancedAbortSignalWithFetchArguments(instance, requestInfo, requestInit) {
    if (IsObject(globalThis) && ('AbortController' in globalThis)) {
        const signal = ExtractSignalFromFetchArguments(requestInfo, requestInit);
        const newSignal = (signal === null)
            ? instance
            : AdvancedAbortController.fromAbortSignals(signal, instance).signal;
        requestInit = (requestInit === void 0)
            ? {}
            : { ...requestInit };
        requestInit.signal = newSignal.toAbortController().signal;
    }
    return requestInit;
}
export function LinkAdvancedAbortSignalWithFetchArgumentsSpread(instance, requestInfo, requestInit) {
    return [requestInfo, LinkAdvancedAbortSignalWithFetchArguments(instance, requestInfo, requestInit)];
}
//# sourceMappingURL=functions.js.map